<template>
  <div v-loading="loading" class="nx-page meterenergy-analysis">
    <el-card shadow="never">
      <div slot="header" class="card-head">
        <span>用水量对比分析（抄表）</span>
        <div class="filters">
          <el-date-picker
            v-model="monthRange"
            type="monthrange"
            value-format="yyyyMM"
            range-separator="至"
            start-placeholder="开始月份"
            end-placeholder="结束月份"
            clearable
            size="small"
            class="month-range"
          />
          <el-button type="primary" size="small" @click="load">查询</el-button>
        </div>
      </div>
      <div ref="chartBar" class="chart-box" />
      <div ref="chartShare" class="chart-box mt16" />
      <div ref="chartLine" class="chart-box mt16" />
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { fetchWaterCompare } from './api'

export default {
  name: 'MeterEnergyWaterCompare',
  data() {
    return { loading: false, monthRange: [], data: {}, charts: [] }
  },
  mounted() {
    this.load()
    window.addEventListener('resize', this.resize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
    this.charts.forEach((c) => c.dispose())
  },
  methods: {
    resize() {
      this.charts.forEach((c) => c.resize())
    },
    async load() {
      const params = this.buildQueryParams()
      if (params === null) {
        return
      }
      try {
        this.loading = true
        const res = await fetchWaterCompare(params)
        this.data = (res.data && res.data.data) || {}
        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    buildQueryParams() {
      if (!this.monthRange || this.monthRange.length !== 2) {
        return {}
      }
      const startMonth = this.normalizeMonth(this.monthRange[0])
      const endMonth = this.normalizeMonth(this.monthRange[1])
      const startIndex = this.getMonthIndex(startMonth)
      const endIndex = this.getMonthIndex(endMonth)
      if (startIndex > endIndex) {
        this.$message.warning('开始月份不能大于结束月份')
        return null
      }
      if (endIndex - startIndex + 1 > 24) {
        this.$message.warning('月份范围最多选择24个月')
        return null
      }
      return {
        startMonth,
        endMonth
      }
    },
    renderCharts() {
      this.charts.forEach((c) => c.dispose())
      this.charts = []
      const d = this.data
      const categoryData = d.categoryUsageCompare || []
      const shareData = d.waterTypeSharePercent || []
      const timeData = d.timeCompareResults || []
      const months = this.collectMonths(d.months, categoryData, shareData, timeData)

      this.renderCategoryChart(categoryData, months)
      this.renderShareChart(shareData, months)
      this.renderTimeChart(timeData, months)
    },
    renderCategoryChart(list, months) {
      const categories = this.unique(list.map((x) => x.category))
      const bar = echarts.init(this.$refs.chartBar)
      const series = months.map((month) => ({
        name: month,
        type: 'bar',
        data: categories.map((category) => this.getNumber(list, { category, billingMonth: month }, 'usageAmount'))
      }))
      bar.setOption({
        title: { text: '类目用水量对比（m³）', left: 0, textStyle: { fontSize: 14 } },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          valueFormatter: (value) => `${this.formatNumber(value)} m³`
        },
        legend: { type: 'scroll', data: months, bottom: 0 },
        grid: { left: 96, right: 24, top: 44, bottom: 48, containLabel: true },
        xAxis: { type: 'value', name: 'm³' },
        yAxis: { type: 'category', data: categories },
        series,
        graphic: this.emptyGraphic(!list.length)
      })
      this.charts.push(bar)
    },
    renderShareChart(list, months) {
      const typeNames = this.unique(list.map((x) => x.typeName))
      const share = echarts.init(this.$refs.chartShare)
      const series = typeNames.map((typeName) => ({
        name: typeName,
        type: 'bar',
        stack: 'waterType',
        data: months.map((month) => this.getNumber(list, { typeName, billingMonth: month }, 'percent'))
      }))
      share.setOption({
        title: { text: '用水类型占比（%）', left: 0, textStyle: { fontSize: 14 } },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (items) => {
            const title = items && items.length ? items[0].axisValue : ''
            const rows = (items || []).map((item) => {
              const row = list.find((x) => x.typeName === item.seriesName && x.billingMonth === item.axisValue) || {}
              return `${item.marker}${item.seriesName}：${this.formatNumber(item.value)}%（${this.formatNumber(row.usageAmount)} m³）`
            })
            return [title].concat(rows).join('<br/>')
          }
        },
        legend: { type: 'scroll', data: typeNames, bottom: 0 },
        grid: { left: 48, right: 24, top: 44, bottom: 48, containLabel: true },
        xAxis: { type: 'category', data: months },
        yAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%' } },
        series,
        graphic: this.emptyGraphic(!list.length)
      })
      this.charts.push(share)
    },
    renderTimeChart(list, months) {
      const line = echarts.init(this.$refs.chartLine)
      line.setOption({
        title: { text: '时间维度对比结果', left: 0, textStyle: { fontSize: 14 } },
        tooltip: {
          trigger: 'axis',
          formatter: (items) => {
            const title = items && items.length ? items[0].axisValue : ''
            const rows = (items || []).map((item) => {
              const unit = item.seriesName === '环比变化率' ? '%' : ' m³'
              return `${item.marker}${item.seriesName}：${this.formatNumber(item.value)}${unit}`
            })
            return [title].concat(rows).join('<br/>')
          }
        },
        legend: { data: ['总用水量', '环比变化率'], bottom: 0 },
        grid: { left: 56, right: 56, top: 44, bottom: 48, containLabel: true },
        xAxis: { type: 'category', data: months },
        yAxis: [
          { type: 'value', name: 'm³' },
          { type: 'value', name: '%', axisLabel: { formatter: '{value}%' } }
        ],
        series: [
          {
            name: '总用水量',
            type: 'line',
            smooth: true,
            data: months.map((month) => this.getNumber(list, { billingMonth: month }, 'totalUsageAmount')),
            areaStyle: { color: 'rgba(2,132,199,0.12)' },
            lineStyle: { color: '#0284c7' }
          },
          {
            name: '环比变化率',
            type: 'bar',
            yAxisIndex: 1,
            data: months.map((month) => this.getNumber(list, { billingMonth: month }, 'monthOnMonthPercent')),
            itemStyle: { color: '#14b8a6' }
          }
        ],
        graphic: this.emptyGraphic(!list.length)
      })
      this.charts.push(line)
    },
    collectMonths(months, categoryData, shareData, timeData) {
      const source = []
      if (months && months.length) {
        source.push(...months)
      }
      source.push(...categoryData.map((x) => x.billingMonth))
      source.push(...shareData.map((x) => x.billingMonth))
      source.push(...timeData.map((x) => x.billingMonth))
      return this.unique(source.map((x) => this.normalizeMonth(x))).sort()
    },
    unique(list) {
      return Array.from(new Set((list || []).filter((x) => x !== undefined && x !== null && x !== '')))
    },
    getNumber(list, matcher, field) {
      const row = (list || []).find((item) => Object.keys(matcher).every((key) => this.isSameValue(item[key], matcher[key])))
      return row && row[field] != null ? Number(row[field]) || 0 : 0
    },
    isSameValue(left, right) {
      return String(left) === String(right)
    },
    normalizeMonth(value) {
      return value === undefined || value === null ? '' : String(value)
    },
    getMonthIndex(month) {
      const year = Number(month.slice(0, 4))
      const monthNo = Number(month.slice(4, 6))
      return year * 12 + monthNo
    },
    formatNumber(value) {
      const num = Number(value) || 0
      return Number.isInteger(num) ? String(num) : num.toFixed(2)
    },
    emptyGraphic(show) {
      return show
        ? {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: { text: '暂无数据', fill: '#909399', fontSize: 14 }
          }
        : []
    }
  }
}
</script>

<style scoped>
.card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.filters {
  display: flex;
  gap: 8px;
  align-items: center;
}
.month-range {
  width: 260px;
}
.hint {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 12px;
}
.chart-box {
  height: 280px;
  width: 100%;
}
.mt16 {
  margin-top: 16px;
}
</style>
